import { useEffect, useMemo, useState } from 'react';
import { Command } from 'cmdk';
import { useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowRight,
  CornerDownLeft,
  Gift,
  Package,
  Receipt,
  Search,
  ShoppingCart,
  Truck,
  Users,
} from 'lucide-react';
import { NAV_ITEMS, PALETTE_ONLY_ITEMS } from './nav';
import { api } from '@/lib/api/client';
import { roleLevel, useAuth } from '@/lib/auth';
import { money, smartDate } from '@/lib/format';
import { cn } from '@/lib/cn';

/**
 * ⌘K — the console's real navigation.
 *
 * Two things in one surface: jump to any screen, and find any *record*. The
 * record search runs live across receipts, products, customers, orders, gift
 * cards, purchase orders and vendors, so an operator holding a receipt, a jar
 * or a gift card gets to the right screen without first deciding which section
 * it lives in. That is the whole point — the rail is the map, this is the
 * teleporter.
 *
 * Every domain is queried in parallel and settled independently: a 403 on one
 * (campaigns are manager-gated) must never blank the rest.
 */
export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const navigate = useNavigate();
  const { user } = useAuth();
  const level = roleLevel(user?.role);

  // ⌘K from anywhere, including inside a text field.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((o) => {
          if (o) setQuery('');
          return !o;
        });
      }
      // "/" focuses search only when not already typing somewhere.
      if (e.key === '/' && !isTyping(e.target)) {
        e.preventDefault();
        setOpen(true);
      }
    };
    const onCustom = () => setOpen(true);
    document.addEventListener('keydown', onKey);
    document.addEventListener('kk:command-palette', onCustom);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('kk:command-palette', onCustom);
    };
  }, []);

  const term = query.trim();
  const searching = term.length >= 2;

  // One debounced term drives all three record searches.
  const debounced = useDebounced(term, 180);

  const { data: results, isFetching } = useQuery({
    queryKey: ['palette', debounced],
    enabled: debounced.length >= 2 && open,
    staleTime: 60_000,
    queryFn: async () => {
      // Every leg is settled independently: one domain the operator lacks the
      // capability for (campaigns are manager-gated, for instance) must not
      // blank the whole palette.
      const settle = <T,>(p: Promise<T>, fallback: T) => p.catch(() => fallback);
      const params = { search: debounced, pageSize: 5 };

      const [products, customers, transactions, orders, vendors, giftCards, pos] =
        await Promise.all([
          settle(
            api.get<{
              data: Array<{ id: number | string; name: string; price: number }>;
            }>('/products', { params }),
            { data: [] }
          ),
          settle(
            api.get<{
              data: Array<{ id: number | string; name: string; balance: number }>;
            }>('/customers', { params }),
            { data: [] }
          ),
          settle(
            api.get<{
              rows: Array<{
                id: string;
                total: number;
                occurredAt: string;
                status: string;
              }>;
            }>('/transactions', { params: { search: debounced, limit: 5 } }),
            { rows: [] }
          ),
          settle(
            api.get<{
              data: Array<{
                id: number;
                orderNumber: string;
                status: string;
                customerLastName: string | null;
                estimatedTotalCents: number;
              }>;
            }>('/orders', { params }),
            { data: [] }
          ),
          settle(
            api.get<{ data: Array<{ id: number; name: string; active: boolean }> }>(
              '/vendors',
              { params }
            ),
            { data: [] }
          ),
          settle(
            api.get<{
              data: Array<{
                id: number;
                code: string;
                balanceCents: number;
                status: string;
              }>;
            }>('/gift-cards', { params }),
            { data: [] }
          ),
          settle(
            api.get<{
              data: Array<{
                id: number;
                vendorName: string | null;
                status: string;
                total: number;
              }>;
            }>('/vendors/purchase-orders', { params }),
            { data: [] }
          ),
        ]);

      return {
        products: products.data ?? [],
        customers: customers.data ?? [],
        transactions: transactions.rows ?? [],
        orders: orders.data ?? [],
        vendors: vendors.data ?? [],
        giftCards: giftCards.data ?? [],
        purchaseOrders: pos.data ?? [],
      };
    },
  });

  const jumpItems = useMemo(() => {
    const allowed = [...NAV_ITEMS, ...PALETTE_ONLY_ITEMS].filter(
      (i) => !i.minRole || level >= roleLevel(i.minRole)
    );
    // cmdk's own filtering is switched off while a record search is running
    // (otherwise it would also filter away the server-matched records), so the
    // jump list has to filter itself — a query of "milk" must not leave the
    // whole nav sitting underneath one product.
    if (!searching) return allowed;
    const needle = term.toLowerCase();
    return allowed.filter(
      (i) =>
        i.label.toLowerCase().includes(needle) ||
        i.keywords?.some((k) => k.includes(needle))
    );
  }, [level, searching, term]);

  // Closing always clears. Reopening onto a stale query silently appends the
  // next thing the operator types to the last thing they searched for, which
  // reads as "the palette is broken" rather than "there is old text here".
  const close = () => {
    setOpen(false);
    setQuery('');
  };

  const go = (to: string) => {
    close();
    void navigate({ to });
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[60] flex animate-[fade-in_100ms_ease-out] items-start justify-center bg-black/50 p-4 pt-[12vh] backdrop-blur-[1px]"
      onClick={close}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="border-line-strong bg-surface-raised w-full max-w-xl animate-[pop-in_140ms_var(--ease-out-quint)] overflow-hidden rounded border shadow-2xl shadow-black/50"
      >
        <Command shouldFilter={!searching} loop>
          <div className="border-line flex items-center gap-2 border-b px-3">
            <Search className="text-ink-faint size-4 shrink-0" />
            <Command.Input
              autoFocus
              value={query}
              onValueChange={setQuery}
              placeholder="Search receipts, products, customers, orders, gift cards… or jump to a screen"
              className="text-ink placeholder:text-ink-faint h-11 flex-1 bg-transparent text-sm outline-none"
              onKeyDown={(e) => {
                if (e.key === 'Escape') close();
              }}
            />
            {isFetching ? (
              <span className="text-2xs text-ink-faint font-mono">searching…</span>
            ) : null}
          </div>

          <Command.List className="scrollpane max-h-[52vh] p-1.5">
            <Command.Empty className="text-ink-faint px-3 py-8 text-center text-xs">
              {searching ? 'Nothing matched.' : 'Type to search.'}
            </Command.Empty>

            {results?.products.length ? (
              <Group heading="Products">
                {results.products.map((p) => (
                  <Item
                    key={p.id}
                    icon={<Package className="size-3.5" />}
                    onSelect={() => go(`/products?id=${p.id}`)}
                    right={money(p.price)}
                  >
                    {p.name}
                  </Item>
                ))}
              </Group>
            ) : null}

            {results?.customers.length ? (
              <Group heading="Customers">
                {results.customers.map((c) => (
                  <Item
                    key={c.id}
                    icon={<Users className="size-3.5" />}
                    onSelect={() => go(`/customers?id=${c.id}`)}
                    right={money(c.balance)}
                    rightTone={c.balance > 0 ? 'negative' : undefined}
                  >
                    {c.name}
                  </Item>
                ))}
              </Group>
            ) : null}

            {results?.transactions.length ? (
              <Group heading="Receipts">
                {results.transactions.map((t) => (
                  <Item
                    key={t.id}
                    icon={<Receipt className="size-3.5" />}
                    onSelect={() => go(`/transactions?id=${t.id}`)}
                    right={money(t.total)}
                    rightTone={
                      t.status === 'refund' || t.status === 'voided'
                        ? 'negative'
                        : undefined
                    }
                  >
                    {/* There is no receipt_number column — the operator-facing
                        handle is the id prefix. See FINDINGS.md §5. */}
                    <span className="font-mono">{t.id.slice(0, 8)}</span>
                    <span className="text-2xs text-ink-faint ml-2">
                      {smartDate(t.occurredAt)}
                      {t.status !== 'completed' ? ` · ${t.status}` : ''}
                    </span>
                  </Item>
                ))}
              </Group>
            ) : null}

            {results?.orders.length ? (
              <Group heading="Orders">
                {results.orders.map((o) => (
                  <Item
                    key={o.id}
                    icon={<ShoppingCart className="size-3.5" />}
                    onSelect={() => go(`/orders?id=${o.id}`)}
                    right={money(o.estimatedTotalCents)}
                  >
                    <span className="font-mono">{o.orderNumber}</span>
                    <span className="text-2xs text-ink-faint ml-2">
                      {[o.customerLastName, o.status].filter(Boolean).join(' · ')}
                    </span>
                  </Item>
                ))}
              </Group>
            ) : null}

            {results?.giftCards.length ? (
              <Group heading="Gift cards">
                {results.giftCards.map((g) => (
                  <Item
                    key={g.id}
                    icon={<Gift className="size-3.5" />}
                    onSelect={() => go(`/gift-cards?id=${g.id}`)}
                    right={money(g.balanceCents)}
                  >
                    <span className="font-mono">{g.code}</span>
                    <span className="text-2xs text-ink-faint ml-2">{g.status}</span>
                  </Item>
                ))}
              </Group>
            ) : null}

            {results?.purchaseOrders.length ? (
              <Group heading="Purchase orders">
                {results.purchaseOrders.map((po) => (
                  <Item
                    key={po.id}
                    icon={<Truck className="size-3.5" />}
                    onSelect={() => go(`/purchasing?id=${po.id}`)}
                    right={money(po.total)}
                  >
                    <span className="font-mono">PO-{po.id}</span>
                    <span className="text-2xs text-ink-faint ml-2">
                      {[po.vendorName, po.status].filter(Boolean).join(' · ')}
                    </span>
                  </Item>
                ))}
              </Group>
            ) : null}

            {results?.vendors.length ? (
              <Group heading="Vendors">
                {results.vendors.map((v) => (
                  <Item
                    key={v.id}
                    icon={<Truck className="size-3.5" />}
                    onSelect={() => go(`/vendors?id=${v.id}`)}
                    right={v.active ? undefined : 'inactive'}
                  >
                    {v.name}
                  </Item>
                ))}
              </Group>
            ) : null}

            {jumpItems.length > 0 ? (
              <Group heading="Jump to">
                {jumpItems.map((item) => (
                  <Item
                    // Two entries may open one screen ("Price list" and "Vendor catalogs").
                    key={`${item.to} ${item.label}`}
                    icon={<item.icon className="size-3.5" />}
                    value={`${item.label} ${item.keywords?.join(' ') ?? ''}`}
                    onSelect={() => go(item.to)}
                    right={<ArrowRight className="size-3 opacity-40" />}
                  >
                    {item.label}
                  </Item>
                ))}
              </Group>
            ) : null}
          </Command.List>

          <div className="border-line bg-surface-sunken text-2xs text-ink-faint flex items-center gap-3 border-t px-3 py-1.5 font-mono">
            <span className="flex items-center gap-1">
              <CornerDownLeft className="size-3" /> open
            </span>
            <span>↑↓ move</span>
            <span>esc close</span>
          </div>
        </Command>
      </div>
    </div>
  );
}

function Group({ heading, children }: { heading: string; children: React.ReactNode }) {
  return (
    <Command.Group
      heading={heading}
      className="[&_[cmdk-group-heading]]:text-2xs [&_[cmdk-group-heading]]:text-ink-faint [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:pt-2 [&_[cmdk-group-heading]]:pb-1 [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:tracking-widest [&_[cmdk-group-heading]]:uppercase"
    >
      {children}
    </Command.Group>
  );
}

function Item({
  icon,
  children,
  right,
  rightTone,
  onSelect,
  value,
}: {
  icon: React.ReactNode;
  children: React.ReactNode;
  right?: React.ReactNode;
  rightTone?: 'negative';
  onSelect: () => void;
  value?: string;
}) {
  return (
    <Command.Item
      value={value}
      onSelect={onSelect}
      className={cn(
        'text-ink-muted flex h-8 cursor-pointer items-center gap-2.5 rounded-sm px-2 text-sm select-none',
        'data-[selected=true]:bg-signal-muted data-[selected=true]:text-ink'
      )}
    >
      <span className="text-ink-faint shrink-0">{icon}</span>
      <span className="min-w-0 flex-1 truncate">{children}</span>
      {right ? (
        <span
          className={cn(
            'text-2xs text-ink-faint shrink-0 font-mono tabular-nums',
            rightTone === 'negative' && 'text-negative'
          )}
        >
          {right}
        </span>
      ) : null}
    </Command.Item>
  );
}

function isTyping(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  if (!el) return false;
  return (
    el.tagName === 'INPUT' ||
    el.tagName === 'TEXTAREA' ||
    el.tagName === 'SELECT' ||
    el.isContentEditable
  );
}

function useDebounced<T>(value: T, ms: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return debounced;
}
